import { isIOS } from './utils'

export const setSessionStorageItem = (key: string, value: string) => {
  if (sessionStorage && sessionStorage?.setItem) {
    sessionStorage.setItem(key, value)
  }
}

export const getSessionStorageItem = (key: string): string => {
  if (sessionStorage && sessionStorage?.getItem) {
    return sessionStorage.getItem(key) ?? ''
  }
  return ''
}

export const getAuthHeaders = () => {
  return JSON.parse(sessionStorage.getItem('auth-header') ?? '{}')
}

export const getSecondaryAuthHeaderObject = () => {
  const secondaryAuthHeader = sessionStorage.getItem('secondary-auth-header')
  return secondaryAuthHeader ? JSON.parse(secondaryAuthHeader) : {}
}

export const getAppVersion = () => {
  const authHeaders = getAuthHeaders()
  const { 'app-version': appVersion = '' } = authHeaders || {}
  return appVersion
}

/** App versions ≤ 87 use a single secondary token; > 87 use the full token map. */
export const MAX_APP_VERSION_SINGLE_SECONDARY_TOKEN = 87

export const getSecondaryTokenBridgeFunction = (): 'secondaryToken' | 'allSecondaryTokens' => {
  const appVersion = Number(getAppVersion())
  if (Number.isFinite(appVersion) && appVersion > MAX_APP_VERSION_SINGLE_SECONDARY_TOKEN) {
    return 'allSecondaryTokens'
  }
  return 'secondaryToken'
}

const STATUS_BAR_COLOR = 'statusBarColor'
const STATUS_BAR_THEME = 'statusBarTheme'

export const setStatusBarColor = (color: string) => {
  if (sessionStorage && sessionStorage?.setItem) {
    sessionStorage.setItem(STATUS_BAR_COLOR, color)
  }
}

export const setStatusBarTheme = (theme: string) => {
  if (sessionStorage && sessionStorage?.setItem) {
    sessionStorage.setItem(STATUS_BAR_THEME, theme)
  }
}

export const getStatusBarColor = () => {
  if (sessionStorage && sessionStorage?.getItem) {
    return sessionStorage.getItem(STATUS_BAR_COLOR)
  }
  return null
}

export const getStatusBarTheme = () => {
  if (sessionStorage && sessionStorage?.getItem) {
    return sessionStorage.getItem(STATUS_BAR_THEME)
  }
  return null
}

export const BOTTOM_PADDING = 'bottomPadding'
export const TOP_PADDING = 'topPadding'

// Edge-to-edge feature constants
const MIN_APP_VERSION_FOR_EDGE_TO_EDGE = 57
const BOTTOM_PADDING_FALLBACK = 90
const BOTTOM_PADDING_EXTRA = isIOS() ? 8 : 16
const TOP_PADDING_FALLBACK = 50
const TOP_PADDING_EXTRA = 8

export const setPaddings = ({ bottomPadding, topPadding }: { bottomPadding: number; topPadding: number }) => {
  if (sessionStorage && sessionStorage?.setItem) {
    sessionStorage.setItem(BOTTOM_PADDING, bottomPadding.toString())
    sessionStorage.setItem(TOP_PADDING, topPadding.toString())
  }
}

const getPadding = (storageKey: string, fallback: number, extraPadding: number): number => {
  const appVersion = getAppVersion()

  // Edge-to-edge feature is only available for app version >= 57
  if (appVersion < MIN_APP_VERSION_FOR_EDGE_TO_EDGE) {
    return 0
  }

  // check is for case when sessionStorage is not available (nearly impossible)
  if (!sessionStorage?.getItem) {
    return fallback + extraPadding
  }

  const storedPadding = sessionStorage?.getItem(storageKey)

  // check is for case when there's no padding in sessionStorage
  if (!storedPadding) {
    // case 1: Means app sent other things through app bridge but didnt purposely send paddings
    if (appVersion) {
      return 0
    }
    // case 2: App crashed and didnt send any appbridge related data
    return fallback + extraPadding
  }

  // case 3: App sent paddings through app bridge
  return parseInt(storedPadding, 10) + extraPadding
}

export const getBottomPadding = ({ includeExtraPadding = true }: { includeExtraPadding?: boolean } = {}): number => {
  return getPadding(BOTTOM_PADDING, BOTTOM_PADDING_FALLBACK, includeExtraPadding ? BOTTOM_PADDING_EXTRA : 0)
}

export const getTopPadding = ({ includeExtraPadding = true }: { includeExtraPadding?: boolean } = {}): number => {
  return getPadding(TOP_PADDING, TOP_PADDING_FALLBACK, includeExtraPadding ? TOP_PADDING_EXTRA : 0)
}
